/**
 * Represents possible notify types in a single mask
 */
export enum NotifyTypes {
  // Push notifications
  URGENT_REMINDERS = 1 << 0, // Ex: You are being disabled, designated driver etc.
  COMMUNITY_ANNOUNCEMENTS = 1 << 1, // Ex: School admin announcements like bus cancelled etc.
  MARKETING_UPDATES = 1 << 2, // Ex: How are we doing?, New feature etc.
  SMART_REMINDERS = 1 << 3, // Ex: New connections, New school activity added etc.
  GENERAL_UPDATES = 1 << 4, // All other notifications
  MESSAGE_NOTIFICATIONS = 1 << 5, // New messages (DM, private or public group)
  SIGNUP_NOTIFICATION = 1 << 6, // Signup code notification
}

export const notifyTypes: Record<NotifyTypes, string> = {
  [NotifyTypes.URGENT_REMINDERS]: 'Key Reminders', // Displayed but not editable in Settings UI
  [NotifyTypes.COMMUNITY_ANNOUNCEMENTS]: 'Community Announcements', // Displayed but not editable in Settings UI
  [NotifyTypes.MARKETING_UPDATES]: 'Product Updates',
  [NotifyTypes.SMART_REMINDERS]: 'Personalized Reminders',
  [NotifyTypes.GENERAL_UPDATES]: 'General Updates',
  [NotifyTypes.MESSAGE_NOTIFICATIONS]: 'Message Notifications', // Not used in Settings UI
  [NotifyTypes.SIGNUP_NOTIFICATION]: 'Signup Invite', // Not used in Settings UI
}

export const notifyTypeTitles: Record<NotifyTypes, string> = {
  [NotifyTypes.URGENT_REMINDERS]: 'Reminder',
  [NotifyTypes.COMMUNITY_ANNOUNCEMENTS]: 'Announcement',
  [NotifyTypes.MARKETING_UPDATES]: 'Product Update',
  [NotifyTypes.SMART_REMINDERS]: 'Personalized Reminder',
  [NotifyTypes.GENERAL_UPDATES]: 'General Update',
  [NotifyTypes.MESSAGE_NOTIFICATIONS]: 'New Messages',
  [NotifyTypes.SIGNUP_NOTIFICATION]: 'Signup Invite',
}

/**
 * Notification Properties for Sending and Creating Notifications
 *
 * These interfaces define the properties used for sending SMS, push notifications, and creating notification records.
 * They are used consistently across all notification-related functions within this file.
 *
 * Some fields are made optional to accommodate the variations in data required for different notification types (SMS, push, record creation).
 *
 * (Note: `UserNotificationOutputProps`, defined in a separate file, is exclusively for retrieving saved user notification records.)
 */
export type CreateNotificationProps = {
  notificationType: NotifyTypes
  notificationTitle: string
  notificationBody: string
  notificationActionUrl?: string
  userIds?: string[]
}

export type NotificationDeliveryOptions = {
  sendSms?: boolean
  sendPush?: boolean
  saveRecord?: boolean
}

/**
 * Notification Input Props
 */
export interface CreateNotificationInputProps extends CreateNotificationProps, NotificationDeliveryOptions {}
